import { useCallback, useEffect, useRef, useState } from 'react';
import { Env } from '../../shared';
import { useAdminApi, ApiError } from '../../shared/adminApi';
import {
  CampaignFilters,
  CreateCampaignBody,
  MAX_PAGE_SIZE,
  PromoCampaign,
  PromoCampaignsResponse,
  UpdateCampaignBody,
} from './types';

/**
 * Deal campaigns (`GET /admin/referrals/campaigns`) with their eligible-business
 * set, first-N cap progress and grant/redemption counts.
 *
 * The `personal_default` campaign comes back like any other row, flagged by
 * `kind` — the UI pins it as "the fallback deal" rather than showing it as one
 * campaign among many.
 */
export function useReferralCampaigns(env: Env, filters: CampaignFilters = {}) {
  const { request } = useAdminApi();
  const [data, setData] = useState<PromoCampaignsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const filterKey = JSON.stringify(filters);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const payload = await request<PromoCampaignsResponse>({
          env,
          path: '/admin/referrals/campaigns',
          query: { ...filtersRef.current },
        });
        if (mounted) setData(payload);
      } catch (e) {
        if (mounted) {
          setError(e instanceof ApiError ? e.message : 'Failed to load campaigns');
          setData(null);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request, env, filterKey, tick]);

  return { data, loading, error, refetch };
}

/**
 * Every campaign in one shot, for the code form's deal picker. A referral
 * programme has tens of campaigns, not thousands, so one max-page fetch beats a
 * paged/typeahead picker here — and the picker has to show `personal_default`
 * plus draft campaigns, which a search box would hide.
 */
export function useCampaignOptions(env: Env) {
  return useReferralCampaigns(env, { limit: MAX_PAGE_SIZE, sort: 'name', order: 'asc' });
}

/** Create (`POST`) and edit (`PUT`) deal campaigns. */
export function useCampaignMutations(env: Env) {
  const { request } = useAdminApi();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useCallback(
    async (body: CreateCampaignBody): Promise<PromoCampaign | null> => {
      setSaving(true);
      setError(null);
      try {
        return await request<PromoCampaign>({
          env,
          path: '/admin/referrals/campaigns',
          method: 'POST',
          body,
        });
      } catch (e) {
        setError(e instanceof ApiError ? e.message : 'Could not create the campaign');
        return null;
      } finally {
        setSaving(false);
      }
    },
    [request, env],
  );

  const update = useCallback(
    async (campaignId: string, body: UpdateCampaignBody): Promise<PromoCampaign | null> => {
      setSaving(true);
      setError(null);
      try {
        return await request<PromoCampaign>({
          env,
          path: `/admin/referrals/campaigns/${encodeURIComponent(campaignId)}`,
          method: 'PUT',
          body,
        });
      } catch (e) {
        setError(e instanceof ApiError ? e.message : 'Could not save the campaign');
        return null;
      } finally {
        setSaving(false);
      }
    },
    [request, env],
  );

  return { create, update, saving, error, setError };
}
